import { Clock } from 'lucide-preact'
import { text } from '#contract'
import type { ShopSectionView } from '#contract'
import { durationText } from '../../../i18n/eta.ts'
import { useT } from '../../../i18n/index.ts'
import styles from './products.module.css'
import { useShop } from './shop-context.ts'
import { ProductIcon } from './stepper.tsx'

/** "1 h 30 min · €3.90 delivery": the vendor's time and fee, on cards and section pages. */
function useDeliveryLine(): string {
  const t = useT()
  const { vendor, money } = useShop()
  return t(
    text('shop.deliveryLine', {
      time: durationText(vendor.deliveryMinutes),
      fee: money(vendor.feeCents),
    }),
  )
}

/** A card per section (Grub Food's restaurants): its first dish, name, tagline, time and fee. */
export function SectionCards() {
  const t = useT()
  const { vendor, nav } = useShop()
  const delivery = useDeliveryLine()
  return (
    <div class={styles.cards}>
      {vendor.sections.map((section) => (
        <button
          key={section.id}
          type="button"
          class={styles.sectionCard}
          onClick={() => {
            nav.push({ kind: 'section', section: section.id })
          }}
        >
          <SectionPicture section={section} />
          <span class={styles.info}>
            <span class={styles.name}>{t(section.name)}</span>
            {section.tagline && <span class={styles.tagline}>{t(section.tagline)}</span>}
            <span class={styles.meta}>
              <Clock aria-hidden="true" />
              {delivery}
            </span>
          </span>
        </button>
      ))}
    </div>
  )
}

/** A tile per section (24Seven's aisles): its first product over its name. */
export function SectionTiles() {
  const t = useT()
  const { vendor, nav } = useShop()
  return (
    <div class={styles.tiles}>
      {vendor.sections.map((section) => (
        <button
          key={section.id}
          type="button"
          class={styles.tile}
          onClick={() => {
            nav.push({ kind: 'section', section: section.id })
          }}
        >
          <SectionPicture section={section} />
          {t(section.name)}
        </button>
      ))}
    </div>
  )
}

/** The section's first product on the section's tint; empty for a section with none. */
export function SectionPicture({ section }: { readonly section: ShopSectionView }) {
  const first = section.products[0]
  return (
    <span class={styles.picture} aria-hidden="true">
      {first && <ProductIcon product={first} class={styles.pictureIcon} />}
    </span>
  )
}
